import moment from 'moment';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoRounded';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { ReactComponent as AddIcon } from '~/assets/images/plus-logo.svg';
import { ReactComponent as SshKeyIcon } from '~/assets/images/sshkey-icon.svg';
import { ReactComponent as SyncServerIcon } from '~/assets/images/sync.svg';
import AlertMessage from '~/components/Alerts/AlertMessage';
import CardPagination from '~/components/CardPagination';
import SSHKeysTable, { SSHKey } from '~/components/SSHKeysTable';
import useResponsive from '~/hooks/useResponsive';

import SSHKeysModals from './components/SSHKeysModals';
import useSSHKeyList from './hooks/useSSHKeyList';
import { SSH_SETTING_TEST_IDS } from './utils/constants';
import { getDefaultKeyName } from './utils/helper';
import { HeaderActionsProps, SyncBannerProps } from './utils/types';

const syncStatusSx = (hasMissingKeys: boolean) => ({
  minHeight: 48,
  px: 2,
  backgroundColor: hasMissingKeys ? 'warning.50' : 'success.100',
});

const warningAlertSx = {
  mt: 2,
  border: (theme: Theme) => `1px solid ${theme.palette.divider} !important`,
  borderRadius: 2,
  backgroundColor: 'common.white',
  alignItems: 'center',
  '& .MuiAlert-icon': {
    color: 'info.main',
  },
  '& strong': {
    fontWeight: 700,
  },
};

// Header actions component to display sync and add buttons
const HeaderActions = ({
  hasItems,
  onAdd,
  onSync,
  syncLabel,
  addLabel,
  isSyncPending,
  showSyncButton = true,
  isServerOnline = true,
  isRefreshing = false,
}: HeaderActionsProps) => {
  const { isMobile } = useResponsive();
  const isMobileView = isMobile();

  return (
    <>
      {showSyncButton && hasItems && (
        <Button
          data-testid={SSH_SETTING_TEST_IDS.syncButton}
          variant="outlined"
          startIcon={<SyncServerIcon />}
          onClick={onSync}
          fullWidth={isMobileView}
          disabled={isSyncPending || !isServerOnline || isRefreshing}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {syncLabel}
        </Button>
      )}

      <Button
        data-testid={SSH_SETTING_TEST_IDS.addButton}
        variant="contained"
        onClick={onAdd}
        fullWidth={isMobileView}
        startIcon={<AddIcon />}
        disabled={!isServerOnline || isRefreshing}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {addLabel}
      </Button>
    </>
  );
};

// Banner component to display sync status and last synced time
const SyncBanner = ({
  hasMissingKeys,
  message,
  lastSyncedMessage,
  isMobile,
}: SyncBannerProps) => (
  <Stack
    direction={isMobile ? 'column' : 'row'}
    alignItems={isMobile ? 'flex-start' : 'center'}
    justifyContent="space-between"
    sx={syncStatusSx(hasMissingKeys)}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      {hasMissingKeys ? (
        <ErrorOutlineOutlinedIcon
          fontSize="small"
          sx={{ color: 'warning.500' }}
        />
      ) : (
        <CheckBoxOutlinedIcon fontSize="small" sx={{ color: 'success.main' }} />
      )}

      <Typography
        variant="body2"
        fontWeight={600}
        color={hasMissingKeys ? 'warning.500' : 'success.dark'}
      >
        {message}
      </Typography>
    </Stack>

    <Typography
      variant="body2"
      color="text.secondary"
      marginLeft={isMobile ? '25px !important' : 0}
    >
      {lastSyncedMessage}
    </Typography>
  </Stack>
);

interface SSHKeysProps {
  serverStatus?: string;
  isVps?: boolean;
}

const SSHKeys = ({ serverStatus = '', isVps = false }: SSHKeysProps) => {
  const { t } = useTranslation('selfManagedHosting', {
    keyPrefix: 'sshSettings',
  });

  const { isMobile } = useResponsive();
  const isMobileView = isMobile();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [viewPublicKey, setViewPublicKey] = useState(false);
  const [selectedSSHKey, setSelectedSSHKey] = useState<SSHKey | null>(null);
  const [openDeleteKey, setOpenDeleteKey] = useState(false);

  const {
    keys,
    filteredKeys,
    paginatedKeys,
    searchQuery,
    page,
    rowsPerPage,
    hasItems,
    hasSearchResults,
    hasMissingKeys,
    missingServerKeysCount,
    isTableLoading,
    isSyncPending,
    reAddingKeyId,
    lastSyncedAt,
    hasScannedServer,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    getKeyById,
    syncKeyById,
    syncWithServer,
  } = useSSHKeyList();

  const lastSyncedTime = lastSyncedAt
    ? moment(lastSyncedAt).format('h:mm A')
    : '-';

  const defaultKeyNameToPopulate = useMemo(
    () => getDefaultKeyName(keys),
    [keys],
  );

  const handleViewAction = (id: number) => {
    const key = getKeyById(id);

    if (!key) {
      return;
    }

    setSelectedSSHKey(key);
    setViewPublicKey(true);
  };

  const handleDeleteAction = (id: number) => {
    const key = getKeyById(id);

    if (!key) {
      return;
    }

    setSelectedSSHKey(key);
    setOpenDeleteKey(true);
  };

  const handleRemoveAction = (id: number) => {
    handleDeleteAction(id);
  };

  const handleAddKey = () => {
    setOpenAddModal(true);
  };

  const handleCancelSearch = () => {
    handleSearch('');
  };
  const hasTableSearchResults = hasSearchResults || isTableLoading;
  const isServerOnline = !isVps || !serverStatus || serverStatus === 'active';
  const isRefreshing = isVps && isTableLoading;

  const headerActions = (
    <HeaderActions
      onAdd={handleAddKey}
      onSync={syncWithServer}
      syncLabel={t('syncBtn')}
      addLabel={t('addBtn')}
      isSyncPending={isSyncPending}
      isServerOnline={isServerOnline}
      isRefreshing={isRefreshing}
      showSyncButton
      hasItems={hasItems}
    />
  );

  const syncBanner =
    hasItems && hasScannedServer ? (
      <SyncBanner
        hasMissingKeys={hasMissingKeys}
        message={
          hasMissingKeys
            ? t('notOnServerSummary', { count: missingServerKeysCount })
            : t('allKeysActive')
        }
        lastSyncedMessage={t('lastSynced', {
          time: lastSyncedTime,
        })}
        isMobile={isMobileView}
      />
    ) : undefined;

  const isRemoveAction = selectedSSHKey?.status === 'notOnServer';
  const viewKeyDetails = {
    keyData: {
      key_name: selectedSSHKey?.name ?? '',
      key: selectedSSHKey?.keyValue ?? '',
    },
    createdAt: selectedSSHKey?.createdAt ?? '',
  };

  return (
    <>
      <SSHKeysTable
        sshKeys={paginatedKeys}
        onView={handleViewAction}
        onDelete={handleDeleteAction}
        onReAdd={syncKeyById}
        onRemove={handleRemoveAction}
        reAddingKeyId={reAddingKeyId}
        testId={SSH_SETTING_TEST_IDS.root}
        headerTestId={SSH_SETTING_TEST_IDS.header}
        bannerTestId={SSH_SETTING_TEST_IDS.syncBanner}
        searchInputTestId={SSH_SETTING_TEST_IDS.searchInput}
        icon={<SshKeyIcon />}
        title={t('title')}
        description={t('description')}
        searchQuery={searchQuery}
        onSearch={handleSearch}
        onCancelSearch={handleCancelSearch}
        onAddAction={handleAddKey}
        serverStatus={serverStatus}
        isVps={isVps}
        isRefreshing={isRefreshing}
        actions={headerActions}
        banner={syncBanner}
        pagination={
          <CardPagination
            resultCount={filteredKeys.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
          />
        }
        hasItems={hasItems}
        hasSearchResults={hasTableSearchResults}
        showPaginationWhenNoSearchResults
        isLoading={isTableLoading}
        showStatusColumn={hasScannedServer}
      />

      <AlertMessage
        severity="info"
        icon={<InfoOutlinedIcon />}
        data-testid={SSH_SETTING_TEST_IDS.warning}
        sx={warningAlertSx}
      >
        <Typography variant="body2">
          <Trans
            t={t}
            i18nKey="warningDescription"
            components={{ b: <strong /> }}
          />
        </Typography>
      </AlertMessage>
      <SSHKeysModals
        addOpen={openAddModal}
        setAddOpen={setOpenAddModal}
        defaultKeyName={defaultKeyNameToPopulate}
        selectedKey={selectedSSHKey}
        viewOpen={viewPublicKey}
        setViewOpen={setViewPublicKey}
        viewKeyDetails={viewKeyDetails}
        isViewKeyLoading={false}
        deleteOpen={openDeleteKey}
        setDeleteOpen={setOpenDeleteKey}
        isRemoveAction={isRemoveAction}
      />
    </>
  );
};

export default SSHKeys;
